import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Menu from '../Menu';
import { useNavigate } from 'react-router-dom';
import SuccessMessage from '../../components/successMessage';
import FailedMessage from '../../components/FailedMessage';
import Switch from '../../components/Switch';
import useApiUrls from '../../useApiUrls';
import Pagination from '../../components/Pagination';

const Audio = () => {
  const [data, setData] = useState([]);
  const [message, setMessage] = useState("");
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();
  const audioRef = useRef(null);
  const token = getCookie("token");
  const userid = getCookie("userid");
  const [publishInfo, setPublishInfo] = useState({});
  const [copySuccess, setCopySuccess] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [copied, setCopied] = useState(false); // Added for copy functionality
  const [dataLimit, setDataLimit] = useState(100);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  // New state for audio popup
  const [showAudioPopup, setShowAudioPopup] = useState(false);
  const [selectedAudioId, setSelectedAudioId] = useState(null);
  const [selectedAudioName, setSelectedAudioName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Function to fetch audio data
  const fetchCatelogData = async () => {
    const requestBody = {
      id: 0,
      applycreateddate: false,
      fromcreateddate: 0,
      endcreateddate: 0,
      active: "",
      name: "",
      ispageload: false,
      pagesize: dataLimit,
      currentpageno: count,
      withoutpagination: false,
      pagedirection: "string", // Changed from "first" to "string" to match API expectation
    };

    try {
      // Check if the operation name is configured, otherwise use the direct URL
      let AudioSearchUrl;
      try {
        AudioSearchUrl = getUrlByOperation("AudioPackOperation/Search");
      } catch (e) {
        // Fallback to direct URL if operation name doesn't exist in the mapping
        AudioSearchUrl = "https://api.dqr-display-tms.eventum.co.in/api/AudioPackOperation/SubUser/Search";
      }
      
      const response = await axios.post('https://api.dqr-display-tms.eventum.co.in/api/AudioPackOperation/SubUser/Search', requestBody, {
        headers: {
          'accept': 'text/plain',
          'X-Authorization': token,
          'UserId': userid,
          'Content-Type': 'application/json-patch+json',
        }
      });

      if (response.data && Array.isArray(response.data.data)) {
        const rows = response.data.data;
        setData(rows);
        setHasMore(rows.length === Number(dataLimit));
        // Calculate approximate total items
        const approximateTotal = (count * Number(dataLimit)) + rows.length + (rows.length === Number(dataLimit) ? Number(dataLimit) : 0);
        setTotalItems(approximateTotal);
      } else {
        setData([]);
        setHasMore(false);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching Catalogue data:", error);
      console.error("Error details:", error.response?.data || error.message);
      setHasMore(false);
      setTotalItems(0);
    }
  };

  // Fetch data on initial load and when pagination changes
  useEffect(() => {
    fetchCatelogData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLimit, count]);

  const handleAddPop = () => {
    navigate("/addaudioPackage");
  };
  
  // Pagination navigation functions
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setDataLimit(newItemsPerPage);
    setCount(0); // Reset to first page when changing items per page
  };

  const goFirst = () => {
    if (count !== 0) setCount(0);
  };
  
  const goPrev = () => {
    if (count > 0) setCount(count - 1);
  };
  
  const goNext = () => {
    if (hasMore) setCount(count + 1);
  };

  const handlePageChange = (page) => {
    setCount(page - 1); // Convert 1-based page to 0-based index
  };

  // Updated handleAddImagePop function to show popup
  const handleAddImagePop = (id, name) => {
    setSelectedAudioId(id);
    setSelectedAudioName(name);
    setShowAudioPopup(true);
  };

  // Function to handle file selection
  const handleFileSelect = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  // Function to handle form submission
  const handleAudioUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setMessage("Please select an audio file");
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('AudioPackId', selectedAudioId);
    formData.append('Remark', selectedAudioName);
    formData.append('Active', 'true');
    formData.append('Name', selectedFile.name);
    formData.append('File', selectedFile);
    
    try {
      const response = await axios.post(
        'https://api.dqr-display-tms.eventum.co.in/api/AudioPackInfoOperation/SubUser/Insert', 
        formData, 
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid || '1',
            'X-Authorization': token
            // Don't set Content-Type here - it's set automatically with FormData
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      if (response.data && response.data.isresponse) {
        setMessage("Audio uploaded successfully!");
        setShowAudioPopup(false);
        setSelectedFile(null);
        fetchCatelogData(); // Refresh the list
      } else {
        throw new Error(response.data?.errormsg || "Failed to upload audio");
      }
    } catch (error) {
      console.error("Error uploading audio:", error);
      setMessage(error.message || "Failed to upload audio");
    } finally {
      setUploading(false);
    }
  };

  // Function to close the popup
  const closeAudioPopup = () => {
    setShowAudioPopup(false);
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const handleUpdatePop = (id) => {
    navigate(`/updateaudioPackage/${id}`);
  };

  const handleDelete = async (e) => {
    const requestBody = { id: e.target.id };

    try {
      await axios.post('https://api.dqr-display-tms.eventum.co.in/api/AudioPackOperation/SubUser/Delete', requestBody, {
        headers: {
          'X-Authorization': token,
          'UserId': userid,
          'Content-Type': 'application/json-patch+json',
        }
      });

      setMessage("Data deleted successfully!");
      fetchCatelogData(); // Call search API again to refresh the list

    } catch (error) {
      console.error("Error deleting item:", error);
      setMessage("Failed to delete data");
    }
  };

  const handleToggle = async (e, item) => {
    const apiUrl = item.active ? getUrlByOperation("AudioPackDeactive") : getUrlByOperation("AudioPackActive");
    const requestBody = { id: item.id };

    try {
      await axios.post(apiUrl, requestBody, {
        headers: {
          'X-Authorization': token,
          'UserId': userid,
          'Content-Type': 'application/json-patch+json',
        }
      });
      setData(data.map(d => d.id === item.id ? { ...d, active: !item.active } : d));
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const playAudio = (src, id) => {
    setPlayingAudioId(id);
    if (audioRef.current) {
      audioRef.current.src = src;
      audioRef.current.play();
    }
  };

  const pauseAudio = () => {
    setPlayingAudioId(null);
    if (audioRef.current) {
      audioRef.current.pause();
    }
  };

  const handleAudioEnded = () => {
    setPlayingAudioId(null);
  };
  
  // Add these clipboard fallback functions at the bottom of your file

// Clipboard utility functions
function copyToClipboard(text) {
  // Try to use the Clipboard API first
  if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text)
      .then(() => true)
      .catch(err => {
        console.error('Could not copy text using Clipboard API: ', err);
        return fallbackCopyToClipboard(text);
      });
  } else {
    // Fall back to the textarea method
    return Promise.resolve(fallbackCopyToClipboard(text));
  }
}

function fallbackCopyToClipboard(text) {
  // Create a temporary textarea element
  const textArea = document.createElement('textarea');
  textArea.value = text;
  
  // Make the textarea out of viewport
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  document.body.appendChild(textArea);
  
  // Select and copy
  textArea.focus();
  textArea.select();
  
  let success = false;
  try {
    success = document.execCommand('copy');
  } catch (err) {
    console.error('Failed to copy with fallback method:', err);
  }
  
  // Remove the textarea from DOM
  document.body.removeChild(textArea);
  return success;
}

// Then update your handlePublish function:
const handlePublish = async (id) => {
  setPublishLoading(true);
  try {
    // First, call the API to generate the publish ID
    const response = await axios.post(
      'https://api.dqr-display-tms.eventum.co.in/api/AudioPackOperation_SubUser/Generate/PublishId',
      { id },
      {
        headers: {
          'accept': 'text/plain',
          'UserId': userid || '1',
          'X-Authorization': token,
          'Content-Type': 'application/json-patch+json'
        }
      }
    );

    // Check if the API response is successful
    if (response.data && response.data.isresponse) {
      // Get the publish ID from the response
      const publishId = response.data.publishid || "";
      
      // Generate the publishable URL
      const publishUrl = `${window.location.origin}/campaignpreview-publish/${publishId}`;
      
      // Update state with the publish info
      setPublishInfo({ 
        id, 
        encodedUrl: publishUrl, 
        encodedId: publishId 
      });
      
      // Copy URL to clipboard using our enhanced function
      const copied = await copyToClipboard(publishUrl);
      
      if (copied) {
        setMessage("URL copied to clipboard!");
        setCopySuccess(true);
      } else {
        setMessage("Generated URL: " + publishUrl);
        setCopySuccess(true);
      }
      
      // Refresh the data to get the updated publishid
      fetchCatelogData();
    } else {
      // Handle API error
      throw new Error(response.data?.errormsg || "Failed to generate publish ID");
    }
  } catch (error) {
    console.error("Error generating publish ID:", error);
    setMessage(error.message || "Failed to generate publish URL");
  } finally {
    setPublishLoading(false);
  }
};

// And update your handleCopyClick function:
const handleCopyClick = async (id, publishId) => {
  const publishUrl = `${window.location.origin}/CS80/AudioPackage/${publishId}`;
  
  try {
    const copied = await copyToClipboard(publishUrl);
    
    if (copied) {
      setMessage("URL copied to clipboard!");
      setCopySuccess(true);
      
      // Log to confirm message was set
      console.log("Copy success message set:", "URL copied to clipboard!");
    } else {
      setMessage("URL: " + publishUrl);
      setCopySuccess(true);
      
      // Log to confirm message was set
      console.log("URL message set:", publishUrl);
    }
  } catch (error) {
    console.error("Failed to copy URL:", error);
    setMessage("Failed to copy URL to clipboard");
  }
};


  // const handlePublish = async (id) => {
  //   setPublishLoading(true);
  //   try {
  //     // First, call the API to generate the publish ID
  //     const response = await axios.post(
  //       'https://api.dqr-display-tms.eventum.co.in/api/AudioPackOperation/Generate/PublishId',
  //       { id },
  //       {
  //         headers: {
  //           'accept': 'text/plain',
  //           'UserId': userid || '1',
  //           'X-Authorization': token,
  //           'Content-Type': 'application/json-patch+json'
  //         }
  //       }
  //     );

  //     // Check if the API response is successful
  //     if (response.data && response.data.isresponse) {
  //       // Get the publish ID from the response
  //       const publishId = response.data.publishid || "";
        
  //       // Generate the publishable URL
  //       const publishUrl = `${window.location.origin}/campaignpreview-publish/${publishId}`;
        
  //       // Update state with the publish info
  //       setPublishInfo({ 
  //         id, 
  //         encodedUrl: publishUrl, 
  //         encodedId: publishId 
  //       });
        
  //       // Copy URL to clipboard
  //       await navigator.clipboard.writeText(publishUrl);

  //       setCopySuccess(true);
        
  //       // Refresh the data to get the updated publishid
  //       fetchCatelogData();
  //     } else {
  //       // Handle API error
  //       throw new Error(response.data?.errormsg || "Failed to generate publish ID");
  //     }
  //   } catch (error) {
  //     console.error("Error generating publish ID:", error);
  //     setMessage(error.message || "Failed to generate publish URL");
  //   } finally {
  //     setPublishLoading(false);
  //   }
  // };

  
  

  // const handleCopyClick = (id, publishId) => {
  //   const publishUrl = `${window.location.origin}/CS80/Audio/${publishId}`;
  //   navigator.clipboard.writeText(publishUrl)
  //     .then(() => {
   
  //       setCopySuccess(true);
  //     })
  //     .catch(error => {
  //       console.error("Failed to copy URL:", error);
  //       setMessage("Failed to copy URL to clipboard");
  //     });
  // };
  
  const handleUnPublish = async (id, publishId) => {
    try {
      const response = await axios.post(
        'https://api.dqr-display-tms.eventum.co.in/api/AudioPackOperation/UnPublishId',
        { id },
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid || '1',
            'X-Authorization': token,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );
      
      if (response.data && response.data.isresponse) {
        setMessage("Audio package unpublished successfully!");
        // Refresh the data to update the UI
        fetchCatelogData();
      } else {
        throw new Error(response.data?.errormsg || "Failed to unpublish audio package");
      }
    } catch (error) {
      console.error("Error unpublishing audio package:", error);
      setMessage(error.message || "Failed to unpublish audio package");
    }
  };

   const handleCopyHttp = (key) => {
    setMessage("URL copied to clipboard!");
    const urlToCopy = `https://api.dqr-display-tms.eventum.co.in/api/Share/SubUser/AudioPack/Zip/${key}`;
    navigator.clipboard.writeText(urlToCopy)
      .then(() => {
        setCopied(true);
        setMessage("URL copied to clipboard!");
        setTimeout(() => setCopied(false), 2000); // hide message after 2s
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

    const handleCopyHttps = (key) => {
      setMessage("URL copied to clipboard!");
    const urlToCopy = `https://api.dqr-display-tms.eventum.co.in/api/Share/SubUser/AudioPack/Zip/${key}`;
    navigator.clipboard.writeText(urlToCopy)
      .then(() => {
        setCopied(true);
        setMessage("URL copied to clipboard!");
        setTimeout(() => setCopied(false), 2000); // hide message after 2s
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  // Function to render the publish column
 const renderPublishColumn = (item) => {
    // If the item already has a publishid, show it with a copy button
    if (item.publishid) {
      // Truncate publishid if it's longer than 5 characters
      const displayId = item.publishid.length > 5 
        ? `${item.publishid.substring(0, 5)}...` 
        : item.publishid;
        
      return (
        <div className="d-flex flex-column">
          <div className="mb-2">
            <span className="badge badge-info">{displayId}</span>
          </div>
          <div className="btn-group">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleCopyClick(item.id, item.publishid)}
              title="Copy URL"
            >
              <i className="fas fa-copy mr-1"></i> URL
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handleCopyHttp(item.publishid)}
              title="Copy HTTP URL"
            >
              HTTP
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handleCopyHttps(item.publishid)}
              title="Copy HTTPS URL"
            >
              HTTPS
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleUnPublish(item.id, item.publishid)}
              title="Unpublish"
            >
              <i className="fas fa-eye-slash"></i>
            </button>
          </div>
        </div>
      );
    }
    
    // Check if this item is currently being processed
    if (publishInfo.id === item.id) {
      // Truncate encodedId if it's longer than 5 characters
      const displayId = publishInfo.encodedId.length > 5 
        ? `${publishInfo.encodedId.substring(0, 5)}...` 
        : publishInfo.encodedId;
        
      return (
        <div className="d-flex flex-column">
          <div className="mb-2">
            <span className="badge badge-info">{displayId}</span>
          </div>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleCopyClick(item.id, publishInfo.encodedId)}
            title="Copy URL"
          >
            <i className="fas fa-copy mr-1"></i> Copy URL
          </button>
        </div>
      );
    }
    
    // If no publishid and not currently processing, show the publish button
    return (
      <button
        className="btn btn-sm btn-success"
        onClick={() => handlePublish(item.id)}
        disabled={publishLoading}
      >
        {publishLoading ? (
          <>
            <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
            Publishing...
          </>
        ) : (
          <>
            <i className="fas fa-globe mr-1"></i> Publish
          </>
        )}
      </button>
    );
  };

  return (
    <>
      <Menu />
      <div>
        <div className="wrapper">
          <div className="content-wrapper">
            <section className="content-header">
              <div className="container-fluid">
                <div className="row mb-2">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <h1 className="mb-0">Audio Package</h1>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleAddPop}
                    >
                      Add Audio Package
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section className="content">
              <div className="container-fluid">
                <hr />
                <div className="row">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header"></div>
                      <div className="card-body table-responsive p-0">
                        <table className="table table-hover table-striped">
  <thead className="thead-light">
    <tr>
      <th style={{width: '5%'}} className="text-center">Id</th>
      <th style={{width: '15%'}}>Name</th>
      <th style={{width: '10%'}} className="text-center">File size (KB)</th>
      <th style={{width: '15%'}} className="text-center">Audio List</th>
      <th style={{width: '30%'}} className="text-center">Action</th>
      <th style={{width: '10%'}} className="text-center">Active</th>
      <th style={{width: '15%'}} className="text-center">Publish</th>
    </tr>
  </thead>
  <tbody>
    {data.length > 0 ? (
      data.map((item, index) => (
        <tr key={index}>
          <td className="text-center">{item.id}</td>
          <td>
            <strong>{item.name}</strong>
          </td>
          <td className="text-center">
            {item.filesize ? (item.filesize / 1024).toFixed(2) : 0} KB
          </td>
          <td className="text-center">
            <button
              type="button"
              className="btn btn-sm btn-info"
              onClick={() => navigate(`/audioListPackage/${item.id}`)}
            >
              <i className="fas fa-headphones mr-1"></i>
               {/* Audio list */}
            </button>
          </td>
          <td className="text-center">
            <div className="btn-group">
              <button
                type="button"
                className="btn btn-sm btn-success mr-1"
                onClick={() => handleAddImagePop(item.id, item.name)}
              >
                <i className="fas fa-plus-circle mr-1"></i>
                 {/* Add Audio */}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary mr-1"
                id={item.id}
                onClick={() => handleUpdatePop(item.id)}
              >
                <i className="fas fa-edit mr-1"></i> 
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                id={item.id}
                onClick={handleDelete}
              >
                <i className="fas fa-trash mr-1"></i> 
              </button>
            </div>
          </td>
          <td className="text-center">
            <Switch
              isOn={item.active}
              handleToggle={(e) => handleToggle(e, item)}
              id={item.id}
            />
          </td>
          <td className="text-center">
            {renderPublishColumn(item)}
          </td>
        </tr>
      ))
    ) : (
      <tr>
        <td colSpan="7" className="text-center py-4">
          <div className="empty-state">
            <i className="fas fa-info-circle text-muted mb-2"></i>
            <p>No audio packages found</p>
          </div>
        </td>
      </tr>
    )}
  </tbody>
</table>
                      </div>
                      <Pagination
                        currentPage={count + 1}
                        totalPages={hasMore ? count + 2 : count + 1}
                        onPageChange={handlePageChange}
                        itemsPerPage={Number(dataLimit)}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        totalItems={totalItems}
                        showPageNumbers={false}
                        showItemsPerPageSelector={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Audio Upload Popup */}
      <div className={`modal ${showAudioPopup ? 'd-block' : 'd-none'}`} tabIndex="-1" role="dialog" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Audio to {selectedAudioName}</h5>
              <button type="button" className="close" onClick={closeAudioPopup}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAudioUpload}>
                <div className="form-group">
                  <label htmlFor="audioFileInput">Select Audio File</label>
                  <input 
                    type="file" 
                    className="form-control-file" 
                    id="audioFileInput" 
                    accept="audio/*" 
                    onChange={handleFileSelect}
                    required
                  />
                  <small className="form-text text-muted">
                    Supported formats: MP3, WAV, OGG, etc.
                  </small>
                </div>
                
                {selectedFile && (
                  <div className="mt-3">
                    <p><strong>Selected file:</strong> {selectedFile.name}</p>
                    <p><strong>Size:</strong> {(selectedFile.size / 1024).toFixed(2)} KB</p>
                  </div>
                )}
                
                {uploading && (
                  <div className="progress mt-3">
                    <div 
                      className="progress-bar progress-bar-striped progress-bar-animated" 
                      role="progressbar" 
                      style={{width: `${uploadProgress}%`}} 
                      aria-valuenow={uploadProgress} 
                      aria-valuemin="0" 
                      aria-valuemax="100"
                    >
                      {uploadProgress}%
                    </div>
                  </div>
                )}
                
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeAudioPopup}>
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={!selectedFile || uploading}
                  >
                    {uploading ? 'Uploading...' : 'Upload Audio'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <audio 
        ref={audioRef} 
        controls 
        style={{ display: 'none' }} 
        onEnded={handleAudioEnded}
      >
        <source src="" type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>

      {message && (
  <>
    {/* Debug: Display the current message value */}
    {/* <div style={{display: 'none'}}>Current message: {message}</div> */}
    {message === "URL copied to clipboard!" && <SuccessMessage message={message} />}
    {message === "Data deleted successfully!" || 
     message === "Audio uploaded successfully!" || 
     message === "URL copied to clipboard!" ? (
      <SuccessMessage message={message} />
    ) : message.includes("Failed") ? (
      <FailedMessage message={message} />
    ) : (
      <SuccessMessage message={message} />
    )}
  </>
)}
    </>
  );
};

export default Audio;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}